import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { themeProp } from '@vmos2/utils';
import { useAppType } from '@vmos2/providers';

import { useComponentTheme } from '../useComponentTheme';

type Props = {
  name: string,
  text: string,
  link: string,
  noPadding?: boolean,
  extended?: boolean,
  externalPages?: object,
  route?: { name: string, params: mixed },
  navigateTo?: Function,
  mode?: string,
};

export const Copyright = ({ name, text, link, noPadding, extended, externalPages, navigateTo, route, mode }: Props) => {
  const { t } = useTranslation('copyright');
  const { isKiosk } = useAppType();
  const theme = useTheme();
  const componentTheme = useComponentTheme(theme => theme.components.Copyright, mode);
  const isDarkMode = componentTheme.mode === 'dark';
  const color = isDarkMode ? '#fff' : '#2A3234';
  const logo = isDarkMode ? theme.logo.copyrightImageWhite : theme.logo.copyrightImage;

  const onClick = useCallback(
    (e, url, externalUrl) => {
      e.preventDefault();
      if (externalUrl) {
        return window.open(externalUrl, '_blank');
      }

      return navigateTo(url, { ...route?.params, fromRoute: route?.name });
    },
    [externalPages, navigateTo, route]
  );

  return (
    <Wrapper noPadding={noPadding} data-test="footer-copyright" extended={extended} color={color}>
      {text}
      <Button title={name} onClick={() => !isKiosk && window.open(link, '_blank')} data-test="footer-copyright-link">
        <Logo src={logo} alt={name} />
      </Button>
      {extended && (
        <Links>
          <Button
            title={t('termsConditions')}
            onClick={e => onClick(e, 'terms', get(externalPages, 'termsUrl'))}
            rel="noopener noreferrer"
            data-test="terms-link"
          >
            {t('termsConditions')}
          </Button>
          <Button
            title={t('privacyPolicy')}
            onClick={e => onClick(e, 'privacy', get(externalPages, 'privacyUrl'))}
            rel="noopener noreferrer"
            data-test="privacy-link"
          >
            {t('privacyPolicy')}
          </Button>
        </Links>
      )}
    </Wrapper>
  );
};

const Wrapper = styled('div')`
  display: flex;
  margin-top: auto;
  justify-content: ${({ extended }) => !extended && 'center'};
  padding: ${({ noPadding }) => !noPadding && `20px`};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ color }) => color};
  align-items: center;

  > a {
    color: inherit;
    margin: 0 4px;
    text-decoration: underline;
  }
`;

const Logo = styled('img')`
  max-width: 80px;
`;

const Links = styled('div')`
  margin-left: auto;
  color: ${({ theme }) => theme.color.grey};

  @media (${themeProp('breakpoint.mobile')}) {
    display: flex;
    flex-direction: column;
  }
`;

const Button = styled('a')`
  cursor: pointer;
  line-height: 0;
  margin: 0 4px;

  @media (${themeProp('breakpoint.mobile')}) {
    line-height: inherit;
    margin: 4px 0;
  }
`;
